import { useEffect, useRef, useState } from "react"

/**
 * iPhone mockup frame containing a real autoplay-on-scroll video.
 *
 * Used on /examples to show real QuiteRight video output, and on campaign
 * landing pages as the highest-leverage trust signal — the actual product
 * doing its thing. Plays when scrolled into view, pauses when scrolled out,
 * tap on the screen to play/pause, tap the speaker icon to unmute.
 *
 * Always starts muted (browser autoplay rule). The mute toggle is always
 * visible — user can unmute by tapping the speaker icon, and the unmute
 * preference persists for the lifetime of the page.
 */

const LBTN = {
  background: "#232325",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.07), -1px 0 3px rgba(0,0,0,0.4)",
} as const

const RBTN = {
  background: "#232325",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.07),  1px 0 3px rgba(0,0,0,0.4)",
} as const

interface PropertyVideoPhoneProps {
  videoUrl: string
  poster?: string
  label: string
  /** Width of the phone frame in pixels. Defaults to 260. */
  width?: number
}

export function PropertyVideoPhone({
  videoUrl,
  poster,
  label,
  width = 260,
}: PropertyVideoPhoneProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(true)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play().catch(() => {})
        } else {
          videoRef.current?.pause()
        }
      },
      { threshold: 0.4 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  function togglePlay() {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      v.play().catch(() => {})
    } else {
      v.pause()
    }
  }

  function toggleMute(e: React.MouseEvent) {
    // Don't trigger play/pause when tapping the mute button
    e.stopPropagation()
    const v = videoRef.current
    if (!v) return
    const next = !muted
    v.muted = next
    setMuted(next)
    // If unmuting and the video isn't already playing, kick it off
    if (!next && v.paused) {
      v.play().catch(() => {})
    }
  }

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", width, margin: "0 auto" }}
    >
      {/* Side buttons — purely cosmetic iPhone hardware */}
      <div style={{ position: "absolute", left: -5, top: 100, width: 5, height: 34, borderRadius: "3px 0 0 3px", ...LBTN }} />
      <div style={{ position: "absolute", left: -5, top: 154, width: 5, height: 54, borderRadius: "3px 0 0 3px", ...LBTN }} />
      <div style={{ position: "absolute", left: -5, top: 222, width: 5, height: 54, borderRadius: "3px 0 0 3px", ...LBTN }} />
      <div style={{ position: "absolute", right: -5, top: 142, width: 5, height: 76, borderRadius: "0 3px 3px 0", ...RBTN }} />

      {/* Chassis */}
      <div
        style={{
          background: "linear-gradient(150deg, #252527 0%, #1C1C1E 50%, #141416 100%)",
          borderRadius: 50,
          padding: 9,
          boxShadow: [
            "inset 0 0 0 0.5px rgba(255,255,255,0.13)",
            "0 0 0 1px rgba(0,0,0,0.7)",
            "0 40px 100px rgba(0,0,0,0.45)",
            "0 10px 24px rgba(0,0,0,0.25)",
          ].join(", "),
        }}
      >
        {/* Screen */}
        <div
          style={{
            background: "#000",
            borderRadius: 42,
            overflow: "hidden",
            boxShadow: "inset 0 0 0 0.5px rgba(255,255,255,0.05)",
          }}
        >
          {/* Dynamic island */}
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 13, paddingBottom: 7 }}>
            <div style={{ width: 86, height: 21, background: "#1A1A1C", borderRadius: 100 }} />
          </div>

          {/* Video area */}
          <div
            onClick={togglePlay}
            style={{ position: "relative", aspectRatio: "9/16", cursor: "pointer" }}
          >
            <video
              ref={videoRef}
              src={videoUrl}
              poster={poster}
              muted={muted}
              loop
              playsInline
              preload="metadata"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              aria-label={label}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />

            {/* Play button overlay — fades out during playback */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "opacity 0.25s ease",
                opacity: playing ? 0 : 1,
                pointerEvents: playing ? "none" : "auto",
              }}
            >
              <div
                className="transition-transform hover:scale-110"
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.45)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
                }}
              >
                <svg viewBox="0 0 24 24" fill="white" style={{ width: 26, height: 26, marginLeft: 3 }}>
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>

            {/* Mute / unmute toggle — bottom right of the video, always visible */}
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              aria-pressed={!muted}
              className="transition-transform hover:scale-110"
              style={{
                position: "absolute",
                bottom: 14,
                right: 14,
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.55)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                cursor: "pointer",
                padding: 0,
              }}
            >
              {muted ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                  <path d="M11 5 6 9H2v6h4l5 4z" />
                  <line x1="22" y1="9" x2="16" y2="15" />
                  <line x1="16" y1="9" x2="22" y2="15" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                  <path d="M11 5 6 9H2v6h4l5 4z" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
            </button>
          </div>

          {/* Home indicator */}
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 9, paddingBottom: 13 }}>
            <div style={{ width: 120, height: 5, background: "rgba(255,255,255,0.18)", borderRadius: 100 }} />
          </div>
        </div>
      </div>
    </div>
  )
}
